import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import DOMPurify from 'dompurify';
import { translatedMessage } from '../../service/LanguageService';
import { CompetitionService } from '../../service/CompetitionService';
import useNavProps from '../../hooks/useNavProps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ErrorMessageDisplayComponent from '../../components/ErrorMessageDisplayComponent';
import { ApplicationService } from '../../service/ApplicationService';
import useAuth from '../../hooks/useAuth';
import { UserService } from '../../service/UserService';
import ErrorDialog from '../../components/ErrorDialog';
import { FormFieldService } from '../../service/FormFieldService';
import FormViewDialog from '../../components/form/FormViewDialog';
import CompetitionUtils from '../../utilities/CompetitionUtils';
import CompetitionNationalDetailsComponent from '../../components/competition/CompetitionNationalDetailsComponent';
import CompetitionJobDetailsComponent from '../../components/competition/CompetitionJobDetailsComponent';
import CompetitionJobDetailsOldComponent from '../../components/competition/CompetitionJobDetailsOldComponent';

const CompetitionDetails = () => {
    const [competition, setCompetition] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [viewErrorDialog, setViewErrorDialog] = useState(false);
    const [formFields, setFormFields] = useState([]);
    const [viewFormDialog, setViewFormDialog] = useState(false);

    const toast = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);
    const applicationService = useMemo(() => new ApplicationService(), []);
    const userService = useMemo(() => new UserService(), []);
    const formFieldService = useMemo(() => new FormFieldService(), []);

    const { competitionIdParam } = useParams();
    const navigate = useNavigate();
    const { auth } = useAuth();

    const { setNavPropsValue } = useNavProps();

    useEffect(() => {
        setIsLoading(true);

        const getCompetition = async () => {
            await competitionService.getCompetitionDetails(competitionIdParam)
                .then((competitionData) => {
                    // console.log(competitionData);
                    setCompetition(competitionData);

                    setNavPropsValue('active-competition', competitionData.id, competitionData.name)

                    setIsLoading(false)

                })
                .catch((error) => {
                    setIsLoading(false)
                    setIsError(true)
                    setErrorMessage(error)
                });
        };

        getCompetition();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [competitionService, competitionIdParam]);

    const applyToCompetition = () => {
        if (userService.checkUserForCompetitionRegister(auth.user)) {
            confirmDialog({
                message: translatedMessage('competition.apply.confirm') + ' "' + competition.name + '" ?',
                header: translatedMessage('generic.confirm'),
                acceptClassName: 'p-button-text',
                acceptIcon: 'pi pi-check',
                rejectClassName: 'p-button-text',
                rejectIcon: 'pi pi-times',
                className: 'pcn-confirm-dialog no-icon',
                accept: () => acceptRegistration()
            });
        } else {
            setViewErrorDialog(true)
        }
    }

    const acceptRegistration = () => {
        applicationService.applyToCompetition(competition.id)
            .then((response) => {
                navigate(`/application/${response?.id}`)
                toast.current.show({ severity: 'success', detail: translatedMessage('competition.apply.confirmation') + ' "' + competition.name + '".' });
            })
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    }

    const viewForm = () => {
        formFieldService.getFormFieldListByCompetition(competition.id)
            .then((fields) => {
                setFormFields(fields);
                setViewFormDialog(true);
            })
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    DOMPurify.addHook('afterSanitizeAttributes', function (node) {
        // set all elements owning target to target=_blank
        if ('target' in node) {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener');
        }
    });

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <ProgressSpinner />
            </div>
        );
    } else if (isError) {
        return (
            <div className='w-full flex align-items-center'>
                <ErrorMessageDisplayComponent message={errorMessage} />
            </div>
        )
    } else {
        return (
            <>
                <Toast ref={toast} />
                <ConfirmDialog />
                <div className="grid h-full">
                    <div className="col-12">
                        <div className="card h-full">
                            <div className='w-full grid pcn-data-view-panel'>
                                <div className='col-12'>
                                    <h3 className='mb-1 font-weight-normal'>{competition?.name}</h3>
                                    <div className='mb-3'>
                                        <Button className="pcn-button-slim p-button-text" onClick={viewForm}>
                                            <div className='flex align-items-center'>
                                                <FontAwesomeIcon icon='fa-regular fa-file-text' className="mr-1 " />
                                                <span>{translatedMessage('competition.viewForm')}</span>
                                            </div>
                                        </Button>
                                        {competition.canRegister &&
                                            <Button className="pcn-button-slim p-button-text" onClick={applyToCompetition}>
                                                <div className='flex align-items-center'>
                                                    <FontAwesomeIcon icon='fa-solid fa-file-pen' className="mr-1 " />
                                                    <span>{translatedMessage('competition.apply.register')}</span>
                                                </div>
                                            </Button>
                                        }
                                        <Button className="pcn-button-slim p-button-text" onClick={() => navigate(`/active-competition`)}>
                                            <div className='flex align-items-center'>
                                                <FontAwesomeIcon icon='fa-solid fa-arrow-left' className="mr-1 " />
                                                <span>{translatedMessage('generic.backToList')}</span>
                                            </div>
                                        </Button>
                                    </div>
                                </div>

                                {CompetitionUtils.isNationalCompetition(competition.type) &&
                                    <CompetitionNationalDetailsComponent competition={competition} />
                                }

                                {CompetitionUtils.isJobCompetition(competition.type) && competition?.oldFormat &&
                                    <CompetitionJobDetailsOldComponent competition={competition} />
                                }

                                {CompetitionUtils.isJobCompetition(competition.type) && !competition?.oldFormat &&
                                    <CompetitionJobDetailsComponent competition={competition} />
                                }


                            </div>
                        </div>
                    </div>
                </div>

                <FormViewDialog
                    formFields={formFields}
                    visible={viewFormDialog}
                    closeDialog={() => { setViewFormDialog(false) }}
                />

                <ErrorDialog
                    visible={viewErrorDialog}
                    closeDialog={() => setViewErrorDialog(false)}
                    message={translatedMessage("profile.error.competition.notComplete")}
                    hasLink={true}
                    link={"/my-profile"}
                    linkInfo={translatedMessage("profile.goTo.text")}
                    linkText={translatedMessage('generic.here')}
                />
            </>
        );
    }
};

export default CompetitionDetails;
